// ChampionCard.tsx

import React, { useContext, useEffect, useState } from 'react';
import { SimplifiedChampion, SimplifiedRunePage } from '../../../../gameTypes';
//import './ChampionCard.css';
import GameAssetsContext from '../../../../context/GameAssetContext';
import MinimalRuneDisplay from './Runes/MinimalRuneDisplay';
import { ShardNames } from '../../../../BuildOverview/runeConfig';
import InventoryDisplay from './Items/InventoryDisplay';
import AbilityDisplays from './AbilityButtons/AbilityDisplays';
import CustomComboSection from './CustomCombo/CustomComboSection';
import ChampionImageWithLevel from './HealthBar/ChampionImageWithLevel';
import SimpleHealthManaBar from './HealthBar/SimpleHealthManaBar';
import { GameLog } from '../../Logs/LogsDisplay';
import StatsDisplay2 from './StatsDisplay/StatsDisplay2';
import StatsDisplayExpanded from './StatsDisplay/StatsDisplayExpanded';
import SummonerSpellsDisplay from './Runes/SummonerSpellDisplay';
import SocketContext from '../../../../../NewWebSocket/components/WebSocketAdapter';
import CategoricalSettingsView from './SettingsPanel/CategoricalSettingsView';
import GameStateContext from '../../../GameContainer2/GameStateContext';
import SimpleMatchupDisplay from '../../../../../NewWebSocket/components/SimpleMatchupDisplay';

import './ChampionCardNew.css';
import { getSummonerSpellImageUrl } from '../../../../../StaticSandbox/components/StaticChampionCard';

// Helper function to extract champion configuration
const extractChampionConfig = (champion: SimplifiedChampion) => {
  // Add debugging to understand the actual runePage structure
/*   console.log(`[extractChampionConfig] Champion runePage:`, champion.runePage);
  console.log(`[extractChampionConfig] primaryPath:`, champion.runePage?.primaryPath);
  console.log(`[extractChampionConfig] primaryRunes:`, champion.runePage?.primaryRunes);
  console.log(`[extractChampionConfig] keystone:`, champion.runePage?.keystone);
  console.log(`[extractChampionConfig] secondaryPath:`, champion.runePage?.secondaryPath);
  console.log(`[extractChampionConfig] secondaryRunes:`, champion.runePage?.secondaryRunes); */
  
  // Extract runes from runePage - include style IDs as first element and keystone as second element
  const runes = {
    primary: [champion.runePage?.primaryPath, champion.runePage?.keystone, ...(champion.runePage?.primaryRunes || [])],
    secondary: [champion.runePage?.secondaryPath, ...(champion.runePage?.secondaryRunes || [])],
    shards: champion.runePage?.shards?.map(shard => {
      const shardId = parseInt(shard, 10);
      return isNaN(shardId) ? 0 : shardId;
    }) || [0, 0, 0]
  };
  
  console.log(`[extractChampionConfig] Extracted runes:`, runes);
  
  // Extract items from inventory
  const items = champion.inventory?.items?.map(item => {
    const itemId = parseInt(item.id, 10);
    return isNaN(itemId) ? 0 : itemId;
  }).filter(id => id > 0) || [];

  // Extract skills from abilities
  const skills = {
    q: champion.abilities?.[0]?.pointsInSpell || 0,
    w: champion.abilities?.[1]?.pointsInSpell || 0,
    e: champion.abilities?.[2]?.pointsInSpell || 0,
    r: champion.abilities?.[3]?.pointsInSpell || 0
  };

  return {
    name: champion.name,
    level: champion.level,
    critMode: champion.critMode || 'ExpectedValue',
    runes,
    items,
    skills
  };
};

interface ChampionCardProps {
  champion: SimplifiedChampion;
  regularLogs: GameLog[];
  comboLogs: GameLog[];
  targetChampion: SimplifiedChampion | null;
  onSelectTargetClick: (championId: string) => void;
}

const ChampionCard: React.FC<ChampionCardProps> = ({
  champion,
  regularLogs,
  comboLogs,
  targetChampion,
  onSelectTargetClick,
}) => {

    // Re-add socket context
    const socket = useContext(SocketContext);
    const { gameWideAssets, championsAssets, fetchAndSetChampionAssets } = useContext(GameAssetsContext);
    const { getChampionById } = useContext(GameStateContext);

  const [isCompactView, setIsCompactView] = useState(false); // Start in compact view
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandedStats, setShowExpandedStats] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCombos, setShowCombos] = useState(false);
  const [showCustomCombo, setShowCustomCombo] = useState(true);
  const [activeSettingsCategory, setActiveSettingsCategory] = useState<'champion' | 'runes' | 'items' | 'skillOrder' | null>(null);

  // Clear active category when settings panel is closed
  useEffect(() => {
    if (!showSettings) {
      setActiveSettingsCategory(null);
    }
  }, [showSettings]);

  // Panel states
  const [showDetails, setShowDetails] = useState(true);
  const [showStackingEvents, setShowStackingEvents] = useState(true);
  const [showStatEvents, setShowStatEvents] = useState(true);
  const [showCCEvents, setShowCCEvents] = useState(true);
  
  // Toggle champion config
  const [showChampionSettings, setShowChampionSettings] = useState(false);

  // Add more comprehensive checking for champion data
  const championName = champion?.name || '';
  
  // Debug combo logs
/*   useEffect(() => {
    console.log(`[ChampionCard] Rendering ComboSelector for ${champion.name} with ${comboLogs.length} combo logs`);
    console.log(`[ChampionCard] Combo log types:`, comboLogs.map(log => log.type).reduce((acc, type) => {
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>));
  }, [champion.name, comboLogs]);
   */
  // Comprehensive debugging for champion data
  useEffect(() => {
    if (!champion) {
      //console.error('[ChampionCard] Champion data is null or undefined');
      return;
    }
    
    // Check for missing essential properties
    const requiredProps = ['id', 'name', 'stats', 'runePage', 'inventory', 'abilities'];
    const missingProps = requiredProps.filter(prop => !champion[prop as keyof typeof champion]);
    
    if (missingProps.length > 0) {
      console.error(`[ChampionCard] Champion is missing required properties: ${missingProps.join(', ')}`, champion);
    }
    
   // console.log('[ChampionCard] Rendering champion:', champion.name, 'with data:', champion);
  }, [champion]);

  // Fetch champion assets when name is available
  useEffect(() => {
    if (championName && !championsAssets[championName]) {
      //console.log(`[ChampionCard] Fetching assets for ${championName}`);
      fetchAndSetChampionAssets(championName);
    }
  }, [championName, championsAssets, fetchAndSetChampionAssets]);

  // Add fallback for missing champion data
  if (!champion || !championName) {
    console.error('[ChampionCard] Invalid champion data:', champion);
    return <div className="champion-card error">Invalid champion data</div>;
  }

  if (!championsAssets[championName]) {
    console.log('[ChampionCard] Loading assets for champion:', championName);
    return <div className="champion-card-loading">Loading {championName} assets...</div>;
  }

  // Add safety checks for champion data
  try {
    const { championImageUrl, spellImageUrls, autoAttackImageUrl, passiveImageUrl } = championsAssets[championName];

    // Handle missing champion stats
    if (!champion.stats || !champion.stats.totalStats) {
      console.error('[ChampionCard] Champion stats missing:', champion);
      return <div className="champion-card error">Champion stats missing</div>;
    }

    // Handle missing runePage
    if (!champion.runePage) {
      console.error('[ChampionCard] Champion runePage missing:', champion);
      return <div className="champion-card error">Champion runePage missing</div>;
    }

    // Handle missing abilities
    if (!champion.abilities) {
      console.error('[ChampionCard] Champion abilities missing:', champion);
      return <div className="champion-card error">Champion abilities missing</div>;
    }

    const handleAbilityClick = (abilityKey: string): void => {
      console.log('Ability clicked', abilityKey);
      
      if (socket) {
        try {
          if (abilityKey === 'A') {
            // Send auto attack message
            socket.sendGameMessage('use-auto-attack', {
              championId: champion.id
            });
          } else {
            // Send ability use message
            socket.sendGameMessage('use-ability', {
              championId: champion.id,
              abilityKey: abilityKey
            });
          }
        } catch (error) {
          console.error('[ChampionCard] Error sending ability click:', error);
        }
      } else {
        console.warn('[ChampionCard] Socket not available for ability click');
      }
    };

    const handleSelectTargetClick = () => {
      onSelectTargetClick(champion.id);
    };

    // Handle combo reset - send WebSocket message to reset game state
    const handleResetCombo = () => {
      console.log('Resetting combo for champion:', champion.name);
      
      if (socket) {
        const resetData = {
          championId: champion.id,
          championName: champion.name
        };
        
        console.log('Sending reset combo message:', resetData);
        // Use the sendGameMessage method from WebSocketAdapter
        socket.sendGameMessage('RESET_CHAMPION_COMBO', resetData);
      } else {
        console.warn('WebSocket not available for reset');
        // Fallback: you could also emit a local event or call a callback
        // to clear the logs in the parent component if needed
      }
    };

    // Handle quick access icon clicks
    const handleQuickAccessClick = (categoryId: 'champion' | 'runes' | 'items' | 'skillOrder') => {
      if (activeSettingsCategory === categoryId) {
        // Close if already active
        setActiveSettingsCategory(null);
        setShowSettings(false);
      } else {
        // Open this category
        setActiveSettingsCategory(categoryId);
        setShowSettings(true);
      }
    };

    // Function to render quick access icons
    const renderQuickAccessIcon = (categoryId: 'champion' | 'runes' | 'items' | 'skillOrder', iconSrc: string, alt: string) => {
      // This filter converts to #cdbe91 - the exact same color as the settings button text
      const goldFilter = 'brightness(0) saturate(100%) invert(82%) sepia(8%) saturate(1593%) hue-rotate(19deg) brightness(92%) contrast(86%)';
      
      return (
        <img 
          src={iconSrc} 
          alt={alt} 
          className={`quick-access-icon ${activeSettingsCategory === categoryId ? 'active' : ''}`}
          style={{ 
            width: '16px', 
            height: '16px',
            cursor: 'pointer',
            filter: activeSettingsCategory === categoryId ? goldFilter : 'brightness(0) saturate(100%) invert(60%)',
            transition: 'filter 0.2s ease'
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent settings bar click
            handleQuickAccessClick(categoryId);
          }}
          title={alt}
        />
      );
    };

    // If you want to display runes in MinimalRuneDisplay
    const combineRunePathsAndRunes = (
      runePage: SimplifiedRunePage
    ): { primaryRunes: string[]; secondaryRunes: string[] } => {
      const primaryRunes = [runePage.primaryPath, ...runePage.primaryRunes];
      const secondaryRunes = [runePage.secondaryPath, ...runePage.secondaryRunes];
      return { primaryRunes, secondaryRunes };
    };

    const { primaryRunes, secondaryRunes } = combineRunePathsAndRunes(champion.runePage);

    return (
      <div className={`champion-card ${champion.team} ${isCompactView ? 'compact-view' : 'expanded-view'}`}>
        <div className="info-row">
          <div className="champion-info" onClick={() => setIsCompactView(!isCompactView)}>
            {/* Main row contains champion image + health and abilities */}
            <div className="champion-main-row">
              <div className="champion-image-health-wrapper">
                <ChampionImageWithLevel
                  imageUrl={championImageUrl}
                  name={champion.name}
                  level={champion.level}
                />
                {!isCompactView && (
                  <SimpleHealthManaBar
                    currentHealth={champion.stats.totalStats.currentHealth}
                    maxHealth={champion.stats.totalStats.health}
                    shield={champion.stats.totalStats.shield}
                    magicShield={champion.stats.totalStats.magicShield}
                    physicalShield={champion.stats.totalStats.physicalShield}
                    primaryResource={
                      champion.stats.primaryResource
                        ? {
                            type: champion.stats.primaryResource.type,
                            current: champion.stats.primaryResource.current,
                            max: champion.stats.primaryResource.max
                          }
                        : undefined
                    }
                  />
                )}
              </div>

              <div className="ability-displays-wrapper">
                <AbilityDisplays
                  spellImageUrls={spellImageUrls}
                  autoAttackImageUrl={autoAttackImageUrl}
                  passiveImageUrl={passiveImageUrl}
                  abilities={champion.abilities}
                  attackRange={champion.stats?.totalStats?.attackRange}
                  compactMode={isCompactView}
                  onAbilityClick={handleAbilityClick}
                />
              </div>
            </div>

            {/* Runes and items - will wrap to second row on small screens */}
            <div className="runes-items-grid">
              <div>
                <div className="runes-summoners-section">
                  <div className="summoner-spells">
                {champion.summonerSpells && champion.summonerSpells.length > 0 ? (
                  champion.summonerSpells.map((spell, index) => (
                    <img 
                      key={index}
                      src={getSummonerSpellImageUrl(spell)} 
                      alt={spell} 
                      className="summoner-spell-icon"
                    />
                  ))
                ) : (
                  // Fallback to default spells if none are provided
                  <>
                    <img src={getSummonerSpellImageUrl("Flash")} alt="Flash" className="summoner-spell-icon" />
                    <img src={getSummonerSpellImageUrl("Teleport")} alt="Teleport" className="summoner-spell-icon" />
                  </>
                )}
              </div>
                  <MinimalRuneDisplay
                    key={`runes-${champion.id}-${champion.runePage.primaryRunes.join('-')}-${champion.runePage.secondaryRunes.join('-')}-${champion.runePage.shards.join('-')}`}
                    primaryRunes={primaryRunes}
                    secondaryRunes={secondaryRunes}
                    shards={champion.runePage.shards as ShardNames[]}
                    championId={champion.id}
                    runePage={champion.runePage}
                    champion={champion}
                  />
                </div>
              </div>

              <div>
                <InventoryDisplay
                  itemIds={champion.inventory.items.map((item) => item.id)}
                  bootsSlotItem={champion.inventory.bootsSlotItem?.id || null}
                  hasBotBootsSlot={champion.role === 'bot' && champion.roleQuestCompleted}
                />
              </div>
            </div>

          </div>
        </div>

        
     {/*    <div className="target-display">
              <TargetDisplay
                targetChampion={targetChampion}
                onClick={handleSelectTargetClick}
              />
            </div>

            <div className="damage-breakdown-container">
              <DamageBreakdown logs={regularLogs} targetChampion={targetChampion} />
            </div> */}

       {/*  <SettingsPanel
          showDetails={showDetails}
          setShowDetails={setShowDetails}
          showStackingEvents={showStackingEvents}
          setShowStackingEvents={setShowStackingEvents}
          showStatEvents={showStatEvents}
          setShowStatEvents={setShowStatEvents}
          showCCEvents={showCCEvents}
          setShowCCEvents={setShowCCEvents}
        />

        <div className="champion-config-toggle">
          <button onClick={() => setShowChampionSettings((prev) => !prev)}>
            {showChampionSettings ? "Hide Champion Config" : "Show Champion Config"}
          </button>

          {showChampionSettings && (
            <div className="champion-config-panel">
              <ChampionSettingsView championId={champion.id} champion={champion} />
            </div>
          )}
        </div> */}

   
        
        {/* Settings Section */}
        {!isCompactView && (
          <>
            <div className="settings-bar" onClick={(e) => {
              // Only expand/collapse if clicking on the bar itself, not on icons
              if (e.target === e.currentTarget || (e.target as Element).closest('.settings-expand-button')) {
                setShowSettings(!showSettings);
              }
            }}>
              <div className="settings-content">
                <div className="settings-expand-button" onClick={(e) => {
                  e.stopPropagation();
                  setShowSettings(!showSettings);
                }}>
                  <span className="settings-gear">⚙</span>
                  <span className="settings-title">Settings</span>
                </div>
                
                {/* Quick Access Icons */}
                <div className="quick-access-icons">
                  {renderQuickAccessIcon('runes', gameWideAssets.runeIcon, 'Runes')}
                  {renderQuickAccessIcon('items', gameWideAssets.itemIcon, 'Items')}
                  {renderQuickAccessIcon('skillOrder', gameWideAssets.skillOrderIcon, 'Skill Order')}
                  {renderQuickAccessIcon('champion', gameWideAssets.championIcon, 'Champion')}
                </div>
              </div>
            </div>
            {showSettings && (
              <CategoricalSettingsView
                key={`settings-${champion.id}-${champion.name}`}
                championConfig={{
                  championId: champion.id,
                  championName: champion.name,
                  level: champion.level,
                  team: champion.team,
                  position: champion.position,
                  targetPosition: champion.targetPosition || { x: 0, y: 0 },
                  runes: [],
                  items: [],
                  runePage: champion.runePage,
                  championSpecificSettings: {}
                }}
                champion={champion}
                onChangeConfig={(updatedConfig) => {
                  // Handle config changes if needed
                  console.log("Config changed:", updatedConfig);
                }}
                onSave={(updatedConfig) => {
                  // Handle save action if needed
                  console.log("Config saved:", updatedConfig);
                }}
                onClose={() => {
                  // Close functionality removed - settings always shown
                  console.log("Close action triggered");
                }}
                externalActiveCategory={activeSettingsCategory}
                onCategoryChange={setActiveSettingsCategory}
              />
            )}
            
            {/* Stats Section */}
            <button className="stats-expand-button" onClick={() => setIsExpanded(!isExpanded)}>
              <span className={`stats-arrow ${isExpanded ? 'expanded' : ''}`}>▶</span>
              <span className="stats-title">Stats</span>
            </button>
            
            {isExpanded && (
              <>
                <StatsDisplay2 
                  champion={champion} 
                  onShowExpanded={() => setShowExpandedStats(!showExpandedStats)}
                  showingExpanded={showExpandedStats}
                />
                {showExpandedStats && <StatsDisplayExpanded champion={champion} />}
              </>
            )}
            
            {/* ComboSelector wrapped with ComboDataProvider */}
       {/*      <ComboDataProvider champion={champion}>
              <ComboSelector champion={champion} />
            </ComboDataProvider> */}

            {/* Combos Section */}
            <button className="combos-expand-button" onClick={() => setShowCombos(!showCombos)}>
              <span className={`combos-arrow ${showCombos ? 'expanded' : ''}`}>▶</span>
              <span className="combos-title">Combos vs {targetChampion?.name}</span>
            </button>
            
            {showCombos && (
              <SimpleMatchupDisplay 
                championConfig={extractChampionConfig(champion)}
                targetChampionConfig={targetChampion ? extractChampionConfig(targetChampion) : null}
              />
            )}

            {/* Custom Combo Section */}
            <button className="combos-expand-button" onClick={() => setShowCustomCombo(!showCustomCombo)}>
              <span className={`combos-arrow ${showCustomCombo ? 'expanded' : ''}`}>▶</span>
              <span className="combos-title">Custom Combo</span>
            </button>
            
            {showCustomCombo && (
              <CustomComboSection
                champion={champion}
                logs={regularLogs}
                targetChampion={targetChampion}
                onAbilityClick={handleAbilityClick}
                spellImageUrls={spellImageUrls}
                autoAttackImageUrl={autoAttackImageUrl}
                passiveImageUrl={passiveImageUrl}
                championImageUrl={championImageUrl}
                targetChampionImageUrl={targetChampion ? championsAssets[targetChampion.name]?.championImageUrl : undefined}
                onResetCombo={handleResetCombo}
              />
            )}
          </>
        )}
{/* 
<ComboDisplay
          championName={championName}
          logs={regularLogs}
          showDetails={showDetails}
          showStackingEvents={showStackingEvents}
          showStatEvents={showStatEvents}
          showCCEvents={showCCEvents}
          targetChampionName={targetChampion?.name}
        />

  
        

        <div className="combat-bar">
          <AbilityButtons
            championName={championName}
            spellImageUrls={spellImageUrls}
            autoAttackImageUrl={autoAttackImageUrl}
            passiveImageUrl={passiveImageUrl}
            abilities={champion.abilities}
            onAbilityClick={handleAbilityClick}
          />
        </div> */}


    {/*     <PassiveBar passives={champion.passives} championName={champion.name} /> */}
      
      </div>
    );
  } catch (error) {
    console.error('[ChampionCard] Error handling champion data:', error);
    return <div className="champion-card error">Error handling champion data</div>;
  }
};

export default ChampionCard;
